import { writable } from 'svelte/store';

import { PusherService } from '@client/services/pusher';

const createPusherStore = () => {
  const appendHtmlDefaults = {
    appendHTML: false,
    appendHTMLTemplate: '',
    doNotClearUnknown: true
  };

  const bannerDefaults = {
    altText: '',
    appendImage: false,
    disableRTP: false,
    imagePlaceholder: '',
    removeBanner: false
  };

  const findReplaceDefaults = {
    change: '',
    flags: '',
    regexp: ''
  };

  const signatureDefaults = {
    pushVisual: false,
    signature: null
  };

  const { subscribe, update } = writable({
    pushResults: [],
    successes: 0,
    failures: 0,
    pushing: false
  });

  const reset = () => {
    update((state) => ({ ...state, pushing: false, pushResults: [], successes: 0, failures: 0 }));
  };

  // Google and O365 stream the raw provider error object; every other endpoint streams a string.
  const getFailureMessage = (node, endpoint) => {
    if (typeof node.message === 'string') {
      return node.message;
    }

    if (endpoint === 'gappspushsigs') {
      return node.message?.response?.data?.error_description;
    }

    if (endpoint === 'o365pushsigs') {
      return node.message?.root?.Envelope?.Body?.Fault?.detail?.Message;
    }

    return undefined;
  };

  const push = async (payload, endpoint) => {
    update((state) => ({ ...state, pushing: true, pushResults: [], successes: 0, failures: 0 }));

    return new Promise((resolve) => {
      PusherService.streamParent(endpoint, payload)
        .node('{username}', (node) => {
          update((state) => {
            const success = node.status !== 'failed';
            const message = success ? undefined : getFailureMessage(node, endpoint);

            return {
              ...state,
              pushResults: [...state.pushResults, { username: node.username, success, message }],
              successes: success ? state.successes + 1 : state.successes,
              failures: success ? state.failures : state.failures + 1
            };
          });
        })
        .done(() => {
          let finalState;

          update((state) => {
            finalState = { ...state, pushing: false };
            return finalState;
          });

          resolve(finalState);
        });
    });
  };

  const removeEmptyColumns = (config) => {
    const sigdata = config.csvConfig.sigdata;
    const isNonEmptyString = (str) => str !== null && str !== undefined && str.trim().length > 0;

    const filteredFields = sigdata.fields?.filter(isNonEmptyString);
    const filteredMetaFields = sigdata.meta?.fields?.filter(isNonEmptyString);
    const filteredData = sigdata.data.map((row) =>
      Object.fromEntries(Object.entries(row).filter(([key]) => isNonEmptyString(key)))
    );

    return {
      ...config,
      csvConfig: {
        ...config.csvConfig,
        sigdata: {
          ...sigdata,
          data: filteredData,
          fields: filteredFields,
          meta: {
            ...sigdata.meta,
            fields: filteredMetaFields
          }
        }
      }
    };
  };

  const filterConfig = (config) => {
    config = removeEmptyColumns(config);

    switch (config.mode?.value) {
      case 'appendhtml':
        return {
          ...config,
          ...bannerDefaults,
          ...findReplaceDefaults,
          ...signatureDefaults
        };
      case 'banner':
        return {
          ...config,
          ...appendHtmlDefaults,
          ...findReplaceDefaults,
          ...signatureDefaults
        };
      case 'findandreplace':
        return {
          ...config,
          ...appendHtmlDefaults,
          ...bannerDefaults,
          ...signatureDefaults
        };
      case 'signature':
        return {
          ...config,
          ...appendHtmlDefaults,
          ...bannerDefaults,
          ...findReplaceDefaults
        };
      default:
        return config;
    }
  };

  const pushToO365 = async (config) => {
    const {
      action,
      altText,
      appendHTML,
      appendHTMLTemplate,
      appendImage,
      appendInvisible,
      appendOnly,
      change,
      csvConfig,
      disableRTP,
      flags,
      imagePlaceholder,
      mode,
      prependLines,
      pushVisual,
      regexp,
      removeBanner,
      replaceImage,
      replaceLink,
      selectedtb,
      signatureConfig
    } = filterConfig(config);

    const payload = {
      altText,
      appendHTML,
      appendHTMLTemplate,
      appendImage,
      appendInvisible,
      appendOnly,
      change,
      disableRTP,
      flags,
      imagePlaceholder,
      prependLines,
      pushAction: action?.value,
      pushMode: mode?.value,
      pushVisual,
      regexp,
      removeBanner,
      replaceImage,
      replaceLink,
      rulename: signatureConfig?.rulename,
      sigcode: selectedtb?.sigcode,
      sigdata: csvConfig.sigdata,
      sigtemplate: signatureConfig?.sigTemplate
    };

    return push(payload, 'o365pushsigs');
  };

  const pushToFront = async (config) => {
    const {
      action,
      altText,
      appendHTML,
      appendHTMLTemplate,
      appendImage,
      appendInvisible,
      appendOnly,
      change,
      csvConfig,
      disableRTP,
      doNotClearUnknown,
      flags,
      imagePlaceholder,
      mode,
      prependLines,
      pushVisual,
      regexp,
      removeBanner,
      signatureConfig
    } = filterConfig(config);

    const payload = {
      altText,
      appendHTML,
      appendHTMLTemplate,
      appendImage,
      appendInvisible,
      appendOnly,
      change,
      disableRTP,
      doNotClearUnknown,
      flags,
      imagePlaceholder,
      prependLines,
      pushAction: action?.value,
      pushMode: mode?.value,
      pushVisual,
      regexp,
      removeBanner,
      rulename: signatureConfig?.rulename,
      sigdata: csvConfig.sigdata,
      sigtemplate: signatureConfig?.sigTemplate
    };

    return push(payload, 'frontapppushsigs');
  };

  const pushToGoogle = async (config) => {
    const {
      action,
      altText,
      appendHTML,
      appendHTMLTemplate,
      appendImage,
      appendInvisible,
      appendOnly,
      change,
      csvConfig,
      disableRTP,
      exclusions,
      flags,
      imagePlaceholder,
      mode,
      prependLines,
      pushVisual,
      regexp,
      removeBanner,
      replaceImage,
      replaceLink,
      selectedtb,
      signatureConfig
    } = filterConfig(config);

    const googlegroupbindings = exclusions?.map(({ id }) => ({ id }));

    const payload = {
      altText,
      appendHTML,
      appendHTMLTemplate,
      appendImage,
      appendInvisible,
      appendOnly,
      change,
      disableRTP,
      exclusions: { googlegroupbindings, googleorgbindings: [] },
      flags,
      imagePlaceholder,
      prependLines,
      pushAction: action?.value,
      pushMode: mode?.value,
      pushVisual,
      regexp,
      removeBanner,
      replaceImage,
      replaceLink,
      rulename: signatureConfig?.rulename,
      sigcode: selectedtb?.sigcode,
      sigdata: csvConfig.sigdata,
      sigtemplate: signatureConfig?.sigTemplate
    };

    return push(payload, 'gappspushsigs');
  };

  const pushToOutreach = async (config) => {
    const {
      action,
      altText,
      appendHTML,
      appendHTMLTemplate,
      appendImage,
      appendInvisible,
      appendOnly,
      change,
      csvConfig,
      disableRTP,
      doNotClearUnknown,
      flags,
      imagePlaceholder,
      mode,
      prependLines,
      pushVisual,
      regexp,
      removeBanner,
      signatureConfig
    } = filterConfig(config);

    const payload = {
      altText,
      appendHTML,
      appendHTMLTemplate,
      appendImage,
      appendInvisible,
      appendOnly,
      change,
      disableRTP,
      doNotClearUnknown,
      flags,
      imagePlaceholder,
      prependLines,
      pushAction: action?.value,
      pushMode: mode?.value,
      pushVisual,
      regexp,
      removeBanner,
      rulename: signatureConfig?.rulename,
      sigdata: csvConfig.sigdata,
      sigtemplate: signatureConfig?.sigTemplate
    };

    return push(payload, 'outreachpushsigs');
  };

  const pushToSalesforce = async (config) => {
    const { action, csvConfig, mode, signatureConfig } = filterConfig(config);

    const payload = {
      pushAction: action?.value,
      pushMode: mode?.value,
      rulename: signatureConfig?.rulename,
      sigtemplate: signatureConfig?.sigTemplate,
      sigdata: csvConfig.sigdata
    };

    return push(payload, 'sfdcpushsigs');
  };

  const pushToEmail = async (config) => {
    const { action, csvConfig, emailbody, emailsubject, mode, signatureConfig } = filterConfig(config);

    const payload = {
      emailbody,
      emailsubject,
      pushAction: action?.value,
      pushMode: mode?.value,
      rulename: signatureConfig?.rulename,
      sigdata: csvConfig.sigdata,
      sigtemplate: signatureConfig?.sigTemplate
    };

    return push(payload, 'emailpushsigs');
  };

  return {
    reset,
    subscribe,
    pushToO365,
    pushToFront,
    pushToGoogle,
    pushToOutreach,
    pushToSalesforce,
    pushToEmail
  };
};

export const pusherStore = createPusherStore();
